import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useResubmitSetupApproval } from "../../hooks/useSetupApprovals";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import type { SetupApprovalRequest } from "../../services/setupApprovalApi";

interface ResubmitDialogProps {
  request: SetupApprovalRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DayField =
  | "sowingToGerminationDays"
  | "sowingToSeedlingDays"
  | "sowingToTransplantReadyDays"
  | "sowingToOvergrownDays"
  | "acclimationDays"
  | "plantingToMaturityDays"
  | "harvestingPeriodDays";

const toStr = (val?: number) => (val == null ? "" : String(val));
const parseDay = (val: string) => (val === "" ? undefined : parseInt(val, 10));

interface ResubmitEntityData extends Record<string, unknown> {
  name?: string;
  description?: string;
  isActive?: boolean;
  sowingToGerminationDays?: number;
  sowingToSeedlingDays?: number;
  sowingToTransplantReadyDays?: number;
  sowingToOvergrownDays?: number;
  acclimationDays?: number;
  plantingToMaturityDays?: number;
  harvestingPeriodDays?: number;
  companySeedlingPrice?: number;
  propagationServicePrice?: number;
}

const lifecycleFields: { key: DayField; label: string }[] = [
  { key: "sowingToGerminationDays", label: "Days to Germination" },
  { key: "sowingToSeedlingDays", label: "Days to Seedling" },
  { key: "sowingToTransplantReadyDays", label: "Days to Transplant-Ready" },
  { key: "sowingToOvergrownDays", label: "Days to Overgrown" },
  { key: "acclimationDays", label: "Acclimation Days" },
  { key: "plantingToMaturityDays", label: "Days to Maturity (field)" },
  { key: "harvestingPeriodDays", label: "Harvest Window (days)" },
];

function CropVarietyForm({
  initialData,
  onSubmit,
  isSubmitting,
}: {
  initialData: ResubmitEntityData;
  onSubmit: (entityJson: string) => void;
  isSubmitting: boolean;
}) {
  const [showLifecycle, setShowLifecycle] = useState(false);
  const [formData, setFormData] = useState({
    name: initialData.name ?? "",
    description: initialData.description ?? "",
    isActive: initialData.isActive ?? true,
  });
  const [dayFields, setDayFields] = useState<Record<DayField, string>>({
    sowingToGerminationDays: toStr(initialData.sowingToGerminationDays),
    sowingToSeedlingDays: toStr(initialData.sowingToSeedlingDays),
    sowingToTransplantReadyDays: toStr(initialData.sowingToTransplantReadyDays),
    sowingToOvergrownDays: toStr(initialData.sowingToOvergrownDays),
    acclimationDays: toStr(initialData.acclimationDays),
    plantingToMaturityDays: toStr(initialData.plantingToMaturityDays),
    harvestingPeriodDays: toStr(initialData.harvestingPeriodDays),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: ResubmitEntityData = {
      ...initialData,
      name: formData.name,
      description: formData.description || undefined,
      isActive: formData.isActive,
      sowingToGerminationDays: parseDay(dayFields.sowingToGerminationDays),
      sowingToSeedlingDays: parseDay(dayFields.sowingToSeedlingDays),
      sowingToTransplantReadyDays: parseDay(dayFields.sowingToTransplantReadyDays),
      sowingToOvergrownDays: parseDay(dayFields.sowingToOvergrownDays),
      acclimationDays: parseDay(dayFields.acclimationDays),
      plantingToMaturityDays: parseDay(dayFields.plantingToMaturityDays),
      harvestingPeriodDays: parseDay(dayFields.harvestingPeriodDays),
    };
    onSubmit(JSON.stringify(payload));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field>
        <FieldLabel htmlFor="name">
          Variety Name <span className="text-red-500">*</span>
        </FieldLabel>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          required
          disabled={isSubmitting}
        />
      </Field>

      <Field>
        <FieldLabel htmlFor="description">Description</FieldLabel>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          disabled={isSubmitting}
        />
      </Field>

      <Field>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isActive"
            checked={formData.isActive}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, isActive: checked as boolean }))
            }
            disabled={isSubmitting}
          />
          <Label htmlFor="isActive" className="cursor-pointer">Active</Label>
        </div>
      </Field>

      <div className="border rounded-md">
        <button
          type="button"
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
          onClick={() => setShowLifecycle((v) => !v)}
        >
          Lifecycle Defaults
          {showLifecycle ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        {showLifecycle && (
          <div className="px-4 pb-4 space-y-3 border-t">
            <div className="grid grid-cols-2 gap-3 pt-3">
              {lifecycleFields.map(({ key, label }) => (
                <Field key={key}>
                  <FieldLabel htmlFor={key}>{label}</FieldLabel>
                  <Input
                    id={key}
                    type="number"
                    min={1}
                    placeholder="days"
                    value={dayFields[key]}
                    onChange={(e) =>
                      setDayFields((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>
              ))}
            </div>
          </div>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Resubmitting..." : "Resubmit for Approval"}
      </Button>
    </form>
  );
}

function PricingForm({
  initialData,
  onSubmit,
  isSubmitting,
}: {
  initialData: ResubmitEntityData;
  onSubmit: (entityJson: string) => void;
  isSubmitting: boolean;
}) {
  const [formData, setFormData] = useState({
    companySeedlingPrice: initialData.companySeedlingPrice != null ? String(initialData.companySeedlingPrice) : "",
    propagationServicePrice: initialData.propagationServicePrice != null ? String(initialData.propagationServicePrice) : "",
    isActive: initialData.isActive ?? true,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const company = parseFloat(formData.companySeedlingPrice);
    const propagation = parseFloat(formData.propagationServicePrice);
    const hasCompany = !isNaN(company) && company > 0;
    const hasPropagation = !isNaN(propagation) && propagation > 0;
    if (!hasCompany && !hasPropagation) {
      setErrorMsg("Set at least one price — seedling or propagation.");
      return;
    }
    setErrorMsg(null);
    const payload = {
      ...initialData,
      companySeedlingPrice: hasCompany ? company : null,
      propagationServicePrice: hasPropagation ? propagation : null,
      isActive: formData.isActive,
    };
    onSubmit(JSON.stringify(payload));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errorMsg && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{errorMsg}</div>
      )}
      <p className="text-xs text-muted-foreground">
        Leave a price blank to mark that side as not offered. At least one of the two must be set.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
        <Field>
          <FieldLabel htmlFor="companySeedlingPrice">
            Seedling Price
          </FieldLabel>
          <Input
            id="companySeedlingPrice"
            type="number"
            step="0.01"
            min="0"
            value={formData.companySeedlingPrice}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, companySeedlingPrice: e.target.value }))
            }
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="propagationServicePrice">Propagation Price</FieldLabel>
          <Input
            id="propagationServicePrice"
            type="number"
            step="0.01"
            min="0"
            value={formData.propagationServicePrice}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, propagationServicePrice: e.target.value }))
            }
            disabled={isSubmitting}
          />
        </Field>
      </div>

      <Field>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="isActive"
            checked={formData.isActive}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({ ...prev, isActive: checked as boolean }))
            }
            disabled={isSubmitting}
          />
          <Label htmlFor="isActive" className="cursor-pointer">Active</Label>
        </div>
      </Field>

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? "Resubmitting..." : "Resubmit for Approval"}
      </Button>
    </form>
  );
}

export const ResubmitDialog = ({ request, open, onOpenChange }: ResubmitDialogProps) => {
  const resubmitMutation = useResubmitSetupApproval();
  const notify = useNotification();

  let initialData: ResubmitEntityData = {};
  try {
    initialData = JSON.parse(request.entityJson) as ResubmitEntityData;
  } catch {
    initialData = {};
  }

  const handleResubmit = (entityJson: string) => {
    resubmitMutation.mutate(
      { id: request.id, payload: { entityJson } },
      {
        onSuccess: () => {
          notify.success("Resubmitted for approval");
          onOpenChange(false);
        },
        onError: (error) => {
          notify.error(getErrorMessage(error));
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Amend & Resubmit</DialogTitle>
          <DialogDescription>{request.description}</DialogDescription>
        </DialogHeader>

        {request.rejectionReason && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
              Rejection Reason
            </p>
            <p className="text-sm text-red-600">{request.rejectionReason}</p>
          </div>
        )}

        {request.approvalTypeName === "CropVariety" && (
          <CropVarietyForm
            initialData={initialData}
            onSubmit={handleResubmit}
            isSubmitting={resubmitMutation.isPending}
          />
        )}

        {request.approvalTypeName === "Pricing" && (
          <PricingForm
            initialData={initialData}
            onSubmit={handleResubmit}
            isSubmitting={resubmitMutation.isPending}
          />
        )}

        {request.approvalTypeName === "CropType" && (
          <div className="text-sm text-muted-foreground">
            Crop type resubmission is not yet supported. Please create a new submission.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
